import type { ComputedRef, Ref } from 'vue';
import {
  computed,
  ref,
  watch,
} from 'vue';

import type { IApiFormErrors } from '@modules/core/services/query/types';
import type {
  FormFlattenKey,
  FormFlattenValue,
  FormResult,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import type { ICreateRoleForm, IRolesNamespaces } from '@modules/roles-and-permissions/types';
import { EFormInputTypes } from '@modules/forms/enums';
import { useRolesAndPermissionsTranslate } from '@modules/roles-and-permissions/composables';
import { useCreateRoleMutation } from '@modules/roles-and-permissions/mutations';
import { useRolesAndTreeQuery } from '@modules/roles-and-permissions/queries';
import {
  copyRoleField,
  nameField,
} from '@modules/roles-and-permissions/types';

import type { ICreateRoleEmits } from '../types';

export interface CreateRoleComposable {
  errors: Ref<IApiFormErrors | undefined>;
  isRoleCreateLoading: Ref<boolean>;
  formSchema: ComputedRef<FormSchema<ICreateRoleForm>>;
  formValues: Ref<ICreateRoleForm>;
  clearFormError(key?: string): void;
  handleSubmit(values: ICreateRoleForm): Promise<void>;
  handleChange<T extends ICreateRoleForm, K extends FormFlattenKey<T>>(key: K, value: FormFlattenValue<T, K>, result: FormResult<T>): void;
}

export default function useCreateRole(
  namespace: Ref<IRolesNamespaces>,
  emit: ICreateRoleEmits,
): CreateRoleComposable {
  const formValues = ref<ICreateRoleForm>({
    [nameField]: '',
    [copyRoleField]: 0,
  });

  const { translate } = useRolesAndPermissionsTranslate();

  const { data: rolesAndTreeData, isLoading: isRolesListLoading } = useRolesAndTreeQuery(namespace);

  const {
    mutateAsync: createRole,
    isLoading: isRoleCreateLoading,
    errors,
    clearError,
  } = useCreateRoleMutation();

  const rolesListForCreate = computed<IFormSelectInputOption<number>[]>(() => [
    {
      value: 0,
      label: translate('CREATE_EMPTY_ROLE'),
    },
    ...(rolesAndTreeData.value ? rolesAndTreeData.value.list.map(({ id, name }) => ({ value: id, label: name })) : []),
  ]);

  const formSchema = computed<FormSchema<ICreateRoleForm>>(() => ({
    [nameField]: {
      type: EFormInputTypes.TEXT,
      inputType: 'text',
      label: translate('CREATE_ROLE_NAME'),
      value: formValues.value[nameField],
      required: true,
    },
    [copyRoleField]: {
      type: EFormInputTypes.SELECT,
      label: translate('CREATE_COPY_ROLE'),
      options: rolesListForCreate.value,
      value: formValues.value[copyRoleField],
      loading: isRolesListLoading.value,
    },
  }));

  watch(rolesListForCreate, () => {
    formValues.value[copyRoleField] = 0;
  });

  function resetForm(): void {
    formValues.value = {
      [nameField]: '',
      [copyRoleField]: 0,
    };
  }

  function handleSuccess(roleId: number): void {
    resetForm();
    emit('create-success', roleId);
  }

  async function handleSubmit(values: ICreateRoleForm): Promise<void> {
    const name = values[nameField].trim();

    if (!name)
      return;

    const data: ICreateRoleForm = {
      [nameField]: name.replace(/\s+/g, '_'),
    };

    if (values[copyRoleField] !== 0) {
      data[copyRoleField] = values[copyRoleField];
    }

    try {
      const { role } = await createRole({ role: { ...data, namespace: namespace.value } });
      handleSuccess(role.id);
    } catch {}
  }

  function handleChange<T extends ICreateRoleForm, K extends FormFlattenKey<T>>(
    key: K,
    value: FormFlattenValue<T, K>,
    result: FormResult<T>,
  ): void {
    formValues.value = { ...result.values };
  }

  function clearFormError(key?: string): void {
    if (key) {
      clearError(key);
    }
  }

  return {
    errors,
    isRoleCreateLoading,
    formSchema,
    formValues,
    clearFormError,
    handleSubmit,
    handleChange,
  };
}
